import React, { useState } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { UserPlus, Mail, Trash2, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteUsers({ isOpen, onClose }) {
  const [recipients, setRecipients] = useState([{ email: '', name: '' }]);
  const [personalMessage, setPersonalMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const addRecipient = () => {
    if (recipients.length < 10) {
      setRecipients([...recipients, { email: '', name: '' }]);
    } else {
      toast.error("Maximum 10 invites at a time");
    }
  };

  const removeRecipient = (index) => {
    const newRecipients = recipients.filter((_, i) => i !== index);
    setRecipients(newRecipients.length > 0 ? newRecipients : [{ email: '', name: '' }]);
  };

  const updateRecipient = (index, field, value) => {
    const newRecipients = [...recipients];
    newRecipients[index][field] = value;
    setRecipients(newRecipients);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least one email
    const validRecipients = recipients.filter(r => r.email.trim());
    if (validRecipients.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validRecipients.filter(r => !emailRegex.test(r.email));
    if (invalidEmails.length > 0) {
      toast.error("Please enter valid email addresses");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/invites/send`,
        {
          recipient_emails: validRecipients.map(r => r.email),
          recipient_names: validRecipients.map(r => r.name || null),
          personal_message: personalMessage.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.invites_sent} invite(s) sent successfully! 🎉`);
      
      // Reset form
      setRecipients([{ email: '', name: '' }]);
      setPersonalMessage('');
      onClose();
    } catch (error) {
      toast.error("Failed to send invites. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-white to-emerald-50">
        <DialogHeader className="border-b border-emerald-100 pb-4">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-emerald-600" />
            Invite Friends to PeakMind
          </DialogTitle>
          <DialogDescription className="text-gray-700">
            Share PeakMind with your teammates, friends, or colleagues. Help them improve their mental performance!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Recipients */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold text-gray-800">Recipients</Label>
              <Button
                type="button"
                onClick={addRecipient}
                variant="outline"
                size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                disabled={recipients.length >= 10}
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Add Recipient
              </Button>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {recipients.map((recipient, index) => (
                <div key={index} className="flex items-start gap-2 bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600 mb-1">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="email"
                          placeholder="friend@example.com"
                          value={recipient.email}
                          onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                          className="pl-8"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1">Name (optional)</Label>
                      <Input
                        type="text"
                        placeholder="Friend's name"
                        value={recipient.name}
                        onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  {recipients.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeRecipient(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-6"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              You can invite up to 10 people at once. They'll receive an email invitation to join PeakMind.
            </p>
          </div>

          {/* Personal Message */}
          <div>
            <Label className="text-base font-semibold text-gray-800 mb-2">Personal Message (Optional)</Label>
            <Textarea
              placeholder="Add a personal note to your invitation... (e.g., 'Join me on PeakMind! It's been great for my mental game.')"
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={4}
              className="resize-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Your message will be included in the invitation email.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
            <h4 className="text-sm font-bold text-teal-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              What happens next?
            </h4>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>• Recipients will receive an email invitation from PeakMind</li>
              <li>• The email will include your personal message (if provided)</li>
              <li>• They can click the link to sign up and start using PeakMind</li>
              <li>• You can track your sent invitations in your profile</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-emerald-100">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-gray-300"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg"
              disabled={loading}
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? "Sending..." : "Send Invites"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
